import React, { useRef, useState, useEffect } from 'react';

const LazyVideo: React.FC<{ src: string; alt: string }> = ({ src, alt }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (videoRef.current) observer.observe(videoRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <video
      ref={videoRef}
      src={isVisible ? src : undefined}
      aria-label={alt}
      autoPlay={isVisible}
      loop
      muted
      playsInline
      preload="none"
      className="w-full h-full object-cover transform transition-transform duration-700 group-hover:scale-105"
    />
  );
};

interface FeatureCardProps {
  title: string;
  description: string;
  videoUrl: string;
  imageAlt: string;
  index: number;
  icon: React.ReactNode;
  color: string;
}

const featuresData = [
  {
    title: 'Customization Engine',
    description: 'Empower users to personalize layouts, colors, and content with real-time visual previews instantly.',
    videoUrl: 'https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/omniviewlabs/assets/OtherFeatures/CustomizationAndVisualization1.mp4',
    imageAlt: 'User interface showing live customization and visualization tools',
    color: '#FF5C00', // Brand Orange
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" /></svg>
    )
  },
  {
    title: 'Advanced AI Assistant',
    description: 'Intelligent recommendations and guided product selections powered by AI to enhance buying confidence.',
    videoUrl: 'https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/omniviewlabs/assets/OtherFeatures/AI.mp4',
    imageAlt: 'Abstract animation of a neural network representing an AI assistant',
    color: '#8B5CF6', // Purple
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
    )
  },
  {
    title: 'Order Management',
    description: 'Automated order processing and CRM integration to manage customer relationships effortlessly.',
    videoUrl: 'https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/omniviewlabs/assets/OtherFeatures/OrderManagement2.mp4',
    imageAlt: 'Dashboard visualization showing order tracking and customer management',
    color: '#10B981', // Emerald
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg>
    )
  },
  {
    title: 'E-Commerce Integration',
    description: 'Seamlessly connect with leading platforms. From cart to checkout, ensure a smooth buying journey.',
    videoUrl: 'https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/omniviewlabs/assets/OtherFeatures/IntegrationEcommerce.mp4',
    imageAlt: 'E-commerce integration showing seamless checkout process',
    color: '#003B5C', // Brand Blue
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" /></svg>
    )
  },
];

const FeatureCard: React.FC<FeatureCardProps> = ({ title, description, videoUrl, imageAlt, index, icon, color }) => {
  return (
    <div 
      data-animation="fade-in-up" 
      className={`stagger-${index + 1} group flex flex-col h-full bg-white rounded-[2rem] p-3 border border-slate-100 hover:border-slate-200 hover:shadow-2xl transition-all duration-300 relative overflow-hidden`}
      style={{
        boxShadow: `0 0 0 0 rgba(0,0,0,0)` // Reset
      }}
    >
      {/* Top Accent Line (Hidden by default, reveals on hover) */}
      <div 
        className="absolute top-0 left-0 w-full h-1 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"
        style={{ backgroundColor: color }}
      ></div>

      {/* Media Area */}
      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-[1.5rem] bg-slate-50 border border-slate-100 mb-5">
        <LazyVideo src={videoUrl} alt={imageAlt} />
        
        {/* Colorful Glow Overlay on Hover */}
        <div 
          className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-500 pointer-events-none mix-blend-overlay"
          style={{ background: `linear-gradient(to top right, ${color}, transparent)` }}
        ></div>
      </div>
      
      {/* Text Area */}
      <div className="px-3 pb-4 flex flex-col flex-grow relative">
        <div className="flex items-start gap-4 mb-3">
            {/* Icon Box */}
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 shadow-sm border border-slate-100"
              style={{ backgroundColor: `${color}15`, color: color }} // 15 is approx 10% opacity in hex
            >
              {icon}
            </div>

            <div>
               <h3 className="text-lg font-bold text-[#021226] leading-tight group-hover:text-[#021226] transition-colors">
                {title}
              </h3>
              {/* Decorative underline */}
               <div 
                className="h-0.5 w-0 group-hover:w-12 transition-all duration-300 mt-2 rounded-full opacity-50"
                style={{ backgroundColor: color }}
               ></div>
            </div>
        </div>
        
        <p className="text-sm text-slate-500 leading-relaxed font-medium pl-14">
          {description}
        </p>
      </div>
    </div>
  );
};

const OtherFeatures1: React.FC = () => {
  return (
    <section id="other-features" className="pt-12 pb-20 md:pt-16 md:pb-28 bg-[#F8FAFC] relative overflow-hidden">
      {/* Background Decor */}
      <div className="absolute top-0 left-0 w-full h-full bg-grid opacity-[0.4] pointer-events-none"></div>
      <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-100/40 rounded-full blur-[100px] pointer-events-none"></div>
      <div className="absolute top-1/2 left-10 w-64 h-64 bg-orange-100/40 rounded-full blur-[80px] pointer-events-none"></div>

      <div className="container-xl relative z-10">
        <div className="text-center max-w-4xl mx-auto mb-16 md:mb-20">
          <span className="inline-block px-4 py-1.5 rounded-full bg-white border border-slate-200 text-slate-500 text-[10px] font-black uppercase tracking-[0.3em] mb-6 shadow-sm">
            Platform Capabilities
          </span>
          <h2 data-animation="fade-in-up" className="text-3xl md:text-5xl font-black text-[#021226] tracking-tight leading-tight">
            Everything You Need to <br className="hidden md:block" />
            Sell and Scale in <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF5C00] to-[#FF8A00]">One Place</span>
          </h2>
        </div>

        {/* Compact Grid Layout */}
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6 lg:gap-8">
          {featuresData.map((feature, index) => (
            <FeatureCard key={index} index={index} {...feature} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default OtherFeatures1;